<script setup lang="ts">
import axios from 'axios'
import { LayoutGrid, Plus, Star } from 'lucide-vue-next'

import ReviewTemplatesForm from './ReviewTemplatesForm.vue'
import ReviewTemplatesItem from './ReviewTemplatesItem.vue'
import useReview from '@/composables/review'
import type { reviewReplyTemplateInterface } from '@/types/review'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { moodOptions, moodObject, resolveMoodLabel } = useReview()

const selectedMood = ref(0)
const allTemplates = ref<reviewReplyTemplateInterface[]>([])
const favoriteTemplates = ref<reviewReplyTemplateInterface[]>([])
const isProcessing = ref(true)
const isFavoriteSelected = ref(false)
const addFormIsShown = ref(false)
const pagination = ref({ currentPage: 1, lastPage: 1, total: 0 })

const isTemplateSelected = computed(() => !isFavoriteSelected.value)
const templates = computed(() => (isTemplateSelected.value ? allTemplates.value : favoriteTemplates.value))
const countAllTemplates = computed(() => pagination.value.total)
const countFavoriteTemplates = computed(() => favoriteTemplates.value.length)

const localizedMoodOptions = computed(() => moodOptions.map((option) => ({ id: option.id, text: t(option.text) })))

const emptyStateTitle = computed(() => {
  if (selectedMood.value) {
    const mood = moodObject[selectedMood.value as keyof typeof moodObject]
    return t(`pages.review.reviewSettingsPage.templates.emptyState.mood.${mood}`)
  }

  return isTemplateSelected.value
    ? t('pages.review.reviewSettingsPage.templates.emptyState.all.title')
    : t('pages.review.reviewSettingsPage.templates.emptyState.favorite.title')
})

const emptyStateDescription = computed(() =>
  isTemplateSelected.value
    ? t('pages.review.reviewSettingsPage.templates.emptyState.all.description')
    : t('pages.review.reviewSettingsPage.templates.emptyState.favorite.description')
)

const fetchData = async () => {
  isProcessing.value = true
  try {
    const { data } = await axios.get('/reviews-reply-templates', {
      params: {
        ...(selectedMood.value && { reviewReplyTemplateMoodId: selectedMood.value }),
        page: pagination.value.currentPage
      }
    })
    allTemplates.value = data.data
    pagination.value.total = data.meta.total
    pagination.value.lastPage = data.meta.last_page
    favoriteTemplates.value = allTemplates.value.filter((template) => template.isFavorite)
  } finally {
    isProcessing.value = false
  }
}

const changePage = (page: number) => {
  pagination.value.currentPage = page
  fetchData()
}

const filterByMood = (moodId: number) => {
  selectedMood.value = moodId
  changePage(1)
}

const selectTab = (favorite: boolean) => {
  if (favorite && addFormIsShown.value) {
    addFormIsShown.value = false
  }
  isFavoriteSelected.value = favorite
}

const displayAddForm = () => {
  selectTab(false)
  addFormIsShown.value = !addFormIsShown.value
  filterByMood(0)
}

const templateAdded = () => {
  addFormIsShown.value = false
  fetchData()
}

const replaceInList = (list: reviewReplyTemplateInterface[], template: reviewReplyTemplateInterface) => {
  const index = list.findIndex((item) => item.id === template.id)
  if (index !== -1) {
    list[index] = template
  }
}

const updateTemplate = (template: reviewReplyTemplateInterface) => {
  replaceInList(allTemplates.value, template)
  replaceInList(favoriteTemplates.value, template)
}

const deleteTemplate = (template: reviewReplyTemplateInterface) => {
  allTemplates.value = allTemplates.value.filter((item) => item.id !== template.id)
  favoriteTemplates.value = favoriteTemplates.value.filter((item) => item.id !== template.id)
  pagination.value.total = Math.max(0, pagination.value.total - 1)
}

const toggleTemplateFavorites = (template: reviewReplyTemplateInterface, isFavorite: boolean) => {
  template.isFavorite = isFavorite

  if (isFavorite) {
    if (!favoriteTemplates.value.some((item) => item.id === template.id)) {
      favoriteTemplates.value.push(template)
    }
    return
  }

  favoriteTemplates.value = favoriteTemplates.value.filter((item) => item.id !== template.id)
}

onMounted(() => {
  if (route.query.addTemplate) {
    router.replace({ query: {}, hash: route.hash })
    displayAddForm()
  }
  fetchData()
})
</script>

<template>
  <Card>
    <CardHeader class="border-b">
      <CardTitle>{{ t('pages.review.reviewSettingsPage.templates.title') }}</CardTitle>
      <CardDescription>{{ t('pages.review.reviewSettingsPage.templates.description') }}</CardDescription>
      <CardAction v-if="!addFormIsShown">
        <Button variant="default-primary" @click="displayAddForm">
          <Plus />
          {{ t('pages.review.reviewSettingsPage.templates.buttons.addTemplate') }}
        </Button>
      </CardAction>
    </CardHeader>

    <CardContent>
      <template v-if="!isProcessing">
        <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-2">
            <Button :variant="isTemplateSelected ? 'default-primary' : 'outline'" @click="selectTab(false)">
              <LayoutGrid />
              {{ t('pages.review.reviewSettingsPage.templates.buttons.allMyTemplates') }}
              <Badge variant="secondary">{{ countAllTemplates }}</Badge>
            </Button>
            <Button :variant="isFavoriteSelected ? 'default-primary' : 'outline'" @click="selectTab(true)">
              <Star />
              {{ t('pages.review.reviewSettingsPage.templates.buttons.myFavorites') }}
              <Badge variant="secondary">{{ countFavoriteTemplates }}</Badge>
            </Button>
          </div>

          <ComboboxSelect
            v-if="!addFormIsShown"
            value-key="id"
            label-key="text"
            :placeholder="t('pages.review.reviewSettingsPage.templates.sortMyTemplates')"
            :model-value="selectedMood"
            :options="localizedMoodOptions"
            @update:model-value="filterByMood($event as number)"
          />
        </div>

        <p v-if="selectedMood" class="mb-3 text-sm font-medium text-foreground">
          {{
            t('pages.review.reviewSettingsPage.templates.filteredInfo', {
              templateType: t(resolveMoodLabel(selectedMood) ?? '')
            })
          }}
          <Button variant="link" class="h-auto p-0" @click="filterByMood(0)">
            {{ t('pages.review.reviewSettingsPage.templates.deleteFilter') }}
          </Button>
        </p>

        <ReviewTemplatesForm v-if="addFormIsShown" @canceled="addFormIsShown = false" @created="templateAdded" />

        <div class="flex flex-col gap-3">
          <ReviewTemplatesItem
            v-for="template in templates"
            :key="template.id"
            :template="template"
            :favorites-number="countFavoriteTemplates"
            @updated="updateTemplate"
            @deleted="deleteTemplate(template)"
            @toggle-favorite="toggleTemplateFavorites(template, $event)"
          />
        </div>

        <div v-if="pagination.lastPage > 1 && !isFavoriteSelected" class="mt-6">
          <BasePagination
            :background-color="false"
            :current-page="pagination.currentPage"
            :last-page="pagination.lastPage"
            :total="pagination.total"
            @update:change-page="changePage"
          />
        </div>

        <TheEmptyState
          v-if="templates.length === 0 && !addFormIsShown"
          module="review"
          :title="emptyStateTitle"
          :description="emptyStateDescription"
          :primary-button="{ text: t('pages.review.reviewSettingsPage.templates.buttons.addTemplate') }"
          @on-click="displayAddForm"
        />
      </template>

      <AppSkeleton v-else type="default" />
    </CardContent>
  </Card>
</template>
